"use client";

import { validateStep } from "@repo/questionnaire";
import { useCallback, useEffect, type KeyboardEvent } from "react";

import {
  focusFieldInput,
  getActiveFieldInputIndex,
  getStepFieldInputs,
  shouldIgnoreEnterAdvance,
} from "./questionnaire-keyboard";

import type { QuestionnaireRunnerProps } from "./questionnaire-runner-types";
import type { QuestionnaireState } from "./use-questionnaire-state";
export function useQuestionnaireActions(
  {
    definition,
    onSaveStep,
    onComplete,
    previewMode = false,
    onPreviewStep,
    navigationDisabled = false,
    advanceDisabled = false,
  }: QuestionnaireRunnerProps,
  state: QuestionnaireState,
  onSubmissionSuccess?: () => void,
) {
  const {
    flow,
    answersRef,
    setFieldErrors,
    setActionError,
    setSaving,
    autosaveTimerRef,
    dirtyRef,
    returnToReviewRef,
    reviewStepId,
    moveToStep,
    setSubmitted,
    applyServerAnswers,
    chromeLabel,
    handleContinueRef,
    saving,
    reviewStep,
    fieldsContainerRef,
    requiredComplete,
  } = state;
  const handleContinue = async () => {
    if (navigationDisabled || advanceDisabled) return;
    const validation = validateStep({
      step: flow.currentStep,
      answers: answersRef.current,
    });
    if (!validation.success) {
      setFieldErrors(
        Object.fromEntries(
          validation.errors.map((error) => [error.fieldId, error.message]),
        ),
      );
      return;
    }

    setFieldErrors({});
    setActionError(null);
    setSaving(true);
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    try {
      const proposedNextStepId = flow.nextStep?.id ?? null;

      if (previewMode) {
        const previewAnswers = onPreviewStep
          ? applyServerAnswers(
              await onPreviewStep({
                stepId: flow.currentStep.id,
                answers: answersRef.current,
              }),
            )
          : answersRef.current;
        dirtyRef.current = false;
        if (returnToReviewRef.current && reviewStepId) {
          returnToReviewRef.current = false;
          moveToStep(reviewStepId);
          return;
        }
        if (proposedNextStepId) {
          moveToStep(proposedNextStepId);
          return;
        }
        await onComplete({
          definitionId: definition.id,
          definitionVersion: definition.version,
          answers: previewAnswers,
        });
        setSubmitted(true);
        return;
      }

      const result = await onSaveStep?.({
        definitionId: definition.id,
        definitionVersion: definition.version,
        stepId: flow.currentStep.id,
        answers: answersRef.current,
        stepAnswers: validation.values,
        nextStepId: proposedNextStepId,
        mode: "advance",
      });
      dirtyRef.current = false;
      const localAfterSave = result?.answers
        ? applyServerAnswers(result.answers)
        : answersRef.current;

      if (returnToReviewRef.current && reviewStepId) {
        returnToReviewRef.current = false;
        moveToStep(reviewStepId);
        return;
      }

      const nextStepId =
        result && "nextStepId" in result
          ? result.nextStepId
          : proposedNextStepId;
      if (nextStepId) {
        moveToStep(nextStepId);
        return;
      }

      await onComplete({
        definitionId: definition.id,
        definitionVersion: definition.version,
        answers: localAfterSave,
      });
      onSubmissionSuccess?.();
      setSubmitted(true);
    } catch (error) {
      setActionError(
        chromeLabel(
          error instanceof Error
            ? error.message
            : "Unable to save your answers.",
        ),
      );
    } finally {
      setSaving(false);
    }
  };
  handleContinueRef.current = handleContinue;

  const handleStepKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "Enter" || event.shiftKey || saving || reviewStep) {
        return;
      }

      const target = event.target;
      if (shouldIgnoreEnterAdvance(target)) return;

      if (target instanceof HTMLElement) {
        const choice = target.closest('[data-questionnaire-choice="true"]');
        if (choice) {
          if (
            choice.getAttribute("aria-pressed") === "true" &&
            requiredComplete
          ) {
            event.preventDefault();
            void handleContinueRef.current();
          }
          return;
        }
      }

      const container = fieldsContainerRef.current;
      if (!container) return;

      const focusables = getStepFieldInputs(container);
      const currentIndex = getActiveFieldInputIndex(
        focusables,
        document.activeElement,
      );

      if (currentIndex >= 0 && currentIndex < focusables.length - 1) {
        event.preventDefault();
        const nextInput = focusables[currentIndex + 1];
        if (nextInput) focusFieldInput(nextInput);
        return;
      }

      event.preventDefault();
      void handleContinueRef.current();
    },
    [requiredComplete, reviewStep, saving],
  );

  useEffect(() => {
    if (reviewStep || saving) return;

    const container = fieldsContainerRef.current;
    if (!container) return;

    const focusFirst = () => {
      const first =
        getStepFieldInputs(container)[0] ??
        container.querySelector<HTMLElement>(
          '[data-questionnaire-choice="true"]',
        );
      if (!first) return false;
      focusFieldInput(first);
      return true;
    };
    const observer = new MutationObserver(() => {
      if (focusFirst()) observer.disconnect();
    });
    const timer = window.setTimeout(() => {
      if (!focusFirst())
        observer.observe(container, { childList: true, subtree: true });
    }, 0);
    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [flow.currentStep.id, reviewStep, saving]);

  return { handleContinue, handleStepKeyDown };
}
